/**
 * AnalyticsChart – Time-series chart for orders, revenue, notes, visits, and views.
 * Manager can choose date range via presets or custom interval.
 *
 * @module AnalyticsChart
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { AnalyticsService, TimeSeriesBucket } from '../../services/AnalyticsService';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { getLocalCalendarYMD, parseCalendarYMDToLocalDate } from '../../utils/calendarDate';

const ChartSection = styled.section`
  background: white;
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const ChartHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const ChartTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Spacing.sm};
`;

const PresetGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
`;

const PresetButton = styled.button<{ active?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.md};
  border: 1px solid ${(p) => (p.active ? ThemeColors.primary.main : ThemeColors.border.medium)};
  border-radius: 8px;
  background: ${(p) => (p.active ? ThemeColors.primary.light : 'white')};
  color: ${(p) => (p.active ? 'white' : ThemeColors.text.primary)};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${(p) => (p.active ? ThemeColors.primary.main : ThemeColors.background.secondary)};
  }
`;

const CustomRange = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const ApplyButton = styled.button`
  padding: ${Spacing.xs} ${Spacing.md};
  border: none;
  border-radius: 8px;
  background: ${ThemeColors.primary.main};
  color: white;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${ThemeColors.primary.dark};
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 360px;
  min-height: 280px;
`;

const Loading = styled.div`
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.md};
`;

const Error = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.error.light}20;
  border: 1px solid ${ThemeColors.error.light};
  border-radius: 8px;
  color: ${ThemeColors.error.dark};
  font-size: ${Typography.fontSize.sm};
  margin-bottom: ${Spacing.md};
`;

export type DateRangePreset = '7d' | '30d' | 'month' | '3m' | 'custom';

function getPresetRange(preset: DateRangePreset): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date(end);
  switch (preset) {
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case 'month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case '3m':
      start.setMonth(start.getMonth() - 3);
      break;
    default:
      start.setMonth(start.getMonth() - 1);
  }
  return { start, end };
}

function formatAxisDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const COLORS = {
  orders: ThemeColors.primary.main,
  revenue: ThemeColors.secondary.main,
  notes: ThemeColors.info.main,
  visits: ThemeColors.success.main,
  views: ThemeColors.accent.main
};

export interface AnalyticsChartProps {
  /** Initial preset */
  defaultPreset?: DateRangePreset;
  /** Initial custom start (when defaultPreset is 'custom') */
  defaultStart?: Date;
  /** Initial custom end (when defaultPreset is 'custom') */
  defaultEnd?: Date;
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  defaultPreset = '30d',
  defaultStart,
  defaultEnd
}) => {
  const [preset, setPreset] = useState<DateRangePreset>(defaultPreset);
  const [customStart, setCustomStart] = useState<string>(() =>
    defaultStart ? getLocalCalendarYMD(defaultStart) : ''
  );
  const [customEnd, setCustomEnd] = useState<string>(() =>
    defaultEnd ? getLocalCalendarYMD(defaultEnd) : ''
  );
  const [data, setData] = useState<TimeSeriesBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (start: Date, end: Date) => {
    setLoading(true);
    setError(null);
    try {
      const buckets = await AnalyticsService.getTimeSeriesData(start, end);
      setData(buckets);
    } catch (e: any) {
      setError(e?.message || 'Failed to load chart data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyPreset = useCallback(
    (p: DateRangePreset) => {
      setPreset(p);
      if (p !== 'custom') {
        const { start, end } = getPresetRange(p);
        fetchData(start, end);
      }
    },
    [fetchData]
  );

  const applyCustom = useCallback(() => {
    const start = customStart ? parseCalendarYMDToLocalDate(customStart.trim()) : null;
    let end: Date | null = null;
    if (customEnd) {
      const endDay = parseCalendarYMDToLocalDate(customEnd.trim());
      if (endDay) {
        end = new Date(endDay);
        end.setHours(23, 59, 59, 999);
      }
    }
    if (!start || !end || start > end) {
      setError('Please select a valid start and end date (start ≤ end).');
      return;
    }
    setPreset('custom');
    setError(null);
    fetchData(start, end);
  }, [customStart, customEnd, fetchData]);

  useEffect(() => {
    if (preset === 'custom') return;
    const { start, end } = getPresetRange(preset);
    fetchData(start, end);
  }, [preset, fetchData]);

  const chartData = data.map((b) => ({
    ...b,
    dateLabel: formatAxisDate(b.date)
  }));

  return (
    <ChartSection>
      <ChartHeader>
        <ChartTitle>Metrics Over Time</ChartTitle>
        <Controls>
          <PresetGroup>
            {(['7d', '30d', 'month', '3m'] as const).map((p) => (
              <PresetButton
                key={p}
                active={preset === p}
                onClick={() => applyPreset(p)}
              >
                {p === '7d' && 'Last 7 days'}
                {p === '30d' && 'Last 30 days'}
                {p === 'month' && 'This month'}
                {p === '3m' && 'Last 3 months'}
              </PresetButton>
            ))}
            <PresetButton
              active={preset === 'custom'}
              onClick={() => {
                setPreset('custom');
                if (!customStart || !customEnd) {
                  const end = new Date();
                  const start = new Date(end);
                  start.setDate(start.getDate() - 30);
                  setCustomStart(getLocalCalendarYMD(start));
                  setCustomEnd(getLocalCalendarYMD(end));
                }
              }}
            >
              Custom
            </PresetButton>
          </PresetGroup>
          {preset === 'custom' && (
            <CustomRange>
              <DateInput
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                aria-label="Start date"
              />
              <span>–</span>
              <DateInput
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                aria-label="End date"
              />
              <ApplyButton onClick={applyCustom}>Apply</ApplyButton>
            </CustomRange>
          )}
        </Controls>
      </ChartHeader>

      {error && <Error>{error}</Error>}

      {loading ? (
        <Loading>Loading chart…</Loading>
      ) : chartData.length === 0 ? (
        <Loading>No data for the selected period.</Loading>
      ) : (
        <ChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 30, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={ThemeColors.border.light} />
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: 12, fill: ThemeColors.text.secondary }}
                stroke={ThemeColors.border.medium}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12, fill: ThemeColors.text.secondary }}
                stroke={ThemeColors.border.medium}
                label={{
                  value: 'Count',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: ThemeColors.text.secondary, fontSize: 11 }
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12, fill: ThemeColors.text.secondary }}
                stroke={ThemeColors.border.medium}
                tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                label={{
                  value: 'Revenue',
                  angle: 90,
                  position: 'insideRight',
                  style: { fill: ThemeColors.text.secondary, fontSize: 11 }
                }}
              />
              <Tooltip
                contentStyle={{
                  background: ThemeColors.background.paper,
                  border: `1px solid ${ThemeColors.border.medium}`,
                  borderRadius: 8,
                  boxShadow: Shadows.sm
                }}
                labelFormatter={(_, payload) =>
                  payload?.[0]?.payload?.date
                    ? new Date(payload[0].payload.date + 'T12:00:00').toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : ''
                }
                formatter={((value: number, name: string) => {
                  if (name === 'revenue') return [`$${Number(value).toLocaleString()}`, 'Revenue'];
                  return [value, name.charAt(0).toUpperCase() + name.slice(1)];
                }) as any}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="orders"
                name="orders"
                stroke={COLORS.orders}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                name="revenue"
                stroke={COLORS.revenue}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="notes"
                name="notes"
                stroke={COLORS.notes}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="visits"
                name="visits"
                stroke={COLORS.visits}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="views"
                name="views"
                stroke={COLORS.views}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      )}
    </ChartSection>
  );
};
